/**
 * Adds a single J-code to an order via the backend.
 * @module addJCode
 */

import { API_ENDPOINTS } from "../../enums/apiUrls";
import { throwApiError } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/** Nested J-code object inside the add-J-code request body. */
export interface AddJCodeDetails {
  j_code: string;
  medicine_name: string;
  auth_status: string;
}

/** Request body for the add-J-code endpoint. */
export interface AddJCodePayload {
  order_id: string;
  org_id: string;
  j_code: AddJCodeDetails;
}

/**
 * POSTs a payload that creates a single J-code on the given order.
 *
 * Failures follow the `/prior-auth-utilities/*` HTTP-status contract:
 * 4xx/5xx responses throw an `Error` enriched with `status` and `detail`
 * (read by `utils/showApiError` on the consumer side).
 *
 * @param payload - Identifies the order, org, and J-code to create.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns Parsed JSON response from the server.
 * @throws {Error & { status: number; detail?: string }} On HTTP failure.
 * @throws {Error} On authentication, network, or JSON parse failure.
 */
export const addJCode = async (
  payload: AddJCodePayload,
  signal?: AbortSignal,
) => {
  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.ADD_J_CODE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      ...(signal && { signal }),
    });

    if (!response.ok) {
      await throwApiError(response);
    }
    return await response.json();
  } catch (error) {
    console.error("Failed to add J-code:", error);
    throw error;
  }
};
